import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Palette, Printer } from "lucide-react";

import i18n from "@brand/certificate/i18n.json";
import mediterranean from "@brand/src/mediterranean.json";

import { BrandMark } from "@/components/BrandMark";
import { CertificatePreview } from "@/components/CertificatePreview";
import {
  medColors,
  renderCertificateHtml,
  type CertificateTheme,
} from "@/lib/certificate";
import { verifyBaseUrl } from "@/lib/config";
import { LOCALES } from "@/lib/locale";
import { SAMPLE_TOKEN } from "@/lib/sample";
import { cn } from "@/lib/utils";
import type { Locale, SurfaceRecord } from "@/lib/types";

const FINISH_KEYS = [
  "spatolato",
  "travertino",
  "cemento_industriale",
  "nuvolato",
  "pietra_naturale",
  "calce_antica",
  "epoxy_chromatic",
  "epoxy_metallic",
  "epoxy_art",
  "custom",
] as const;

const SYSTEM_KEYS = ["FLEX", "RADIANT", "CLPH", "R10R12", "UVPLUS", "ZERO_YELLOW"] as const;

const SURFACE_TYPES = ["floor", "wall", "ceiling", "furniture", "wet_area", "exterior"] as const;

const ACADEMY_KEYS = ["none", "gold", "silver", "platinum"] as const;

interface FormState {
  serial: string;
  revision: number;
  issuedAt: string;
  status: SurfaceRecord["status"];
  reviewer: string;
  insurance: string;
  client: string;
  site: string;
  city: string;
  projectName: string;
  surfaceName: string;
  m2: string;
  type: (typeof SURFACE_TYPES)[number];
  finish: (typeof FINISH_KEYS)[number];
  colorRef: string;
  systems: string[];
  company: string;
  academy: (typeof ACADEMY_KEYS)[number];
  patentino: string;
  installed: string;
  collaudo: string;
  yearsAdhesion: number;
  yearsAntiCrack: number;
  yearsUv: number;
  yearsWaterproof: number;
}

const INITIAL: FormState = {
  serial: "VRS-2026-ES0042",
  revision: 1,
  issuedAt: "06/03/2026",
  status: "valid",
  reviewer: "Marco Bellini",
  insurance: "",
  client: "Hotel Boutique Madrid S.L.",
  site: "Calle de Alcalá 45",
  city: "Madrid, ES",
  projectName: "Hotel Boutique Madrid",
  surfaceName: "Lobby & reception floor",
  m2: "186,00",
  type: "floor",
  finish: "spatolato",
  colorRef: "Terra Andalusa",
  systems: ["FLEX", "R10R12"],
  company: "Atelier Superfici S.r.l.",
  academy: "gold",
  patentino: "VRS-AC-2026-0312",
  installed: "18/02/2026",
  collaudo: "04/03/2026",
  yearsAdhesion: 10,
  yearsAntiCrack: 10,
  yearsUv: 5,
  yearsWaterproof: 5,
};

function expiry(collaudo: string, years: number) {
  const [d, m, y] = collaudo.split("/");
  if (!d || !m || !y) return "—";
  return `${d}/${m}/${Number(y) + years}`;
}

function toRecord(f: FormState, locale: Locale): SurfaceRecord {
  const d = i18n[locale];
  const sys = d.system as Record<string, string>;
  const color = medColors.find((c) => c.name === f.colorRef);

  return {
    serial: f.serial,
    revision: f.revision,
    issuedAt: f.issuedAt,
    status: f.status,
    reviewer: f.reviewer,
    insurancePolicyRef: f.insurance || null,
    project: {
      name: f.projectName,
      client: f.client,
      siteAddress: f.site,
      city: f.city,
    },
    surface: {
      name: f.surfaceName,
      m2: f.m2,
      type: f.type,
      typeLabel: d.surfaceType[f.type],
      systemLabel: f.systems.map((s) => sys[s] ?? s).join(" · "),
      finish: f.finish,
      finishLabel: d.finish[f.finish],
      colorRef: f.colorRef,
      colorSwatch: color?.hex ?? null,
    },
    lots: [
      { layer: "Primer", productName: "Versus Anchor P1", lotNumber: "L-24118" },
      { layer: "Geo-matrix", productName: "Versus Matrix FLEX", lotNumber: "L-24203" },
      { layer: "Sealer", productName: "Versus Top Coat 2K", lotNumber: "L-24207" },
    ],
    applicator: {
      companyName: f.company,
      academyLevel: f.academy,
      academyLabel: d.academy[f.academy],
      patentinoNumber: f.patentino,
    },
    dates: { installed: f.installed, collaudo: f.collaudo },
    warranty: [
      {
        kind: "adhesion",
        label: d.warranty.adhesion,
        years: f.yearsAdhesion,
        expiresAt: expiry(f.collaudo, f.yearsAdhesion),
        status: "active",
      },
      {
        kind: "anti_crack",
        label: d.warranty.anti_crack,
        years: f.yearsAntiCrack,
        expiresAt: expiry(f.collaudo, f.yearsAntiCrack),
        status: "active",
      },
      {
        kind: "uv",
        label: d.warranty.uv,
        years: f.yearsUv,
        expiresAt: expiry(f.collaudo, f.yearsUv),
        status: "active",
      },
      {
        kind: "waterproof",
        label: d.warranty.waterproof,
        years: f.yearsWaterproof,
        expiresAt: expiry(f.collaudo, f.yearsWaterproof),
        status: "active",
      },
    ],
    verifyUrl: `${verifyBaseUrl}/${SAMPLE_TOKEN}`,
  };
}

function Text({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <label className="block">
      <span className="field-label">{label}</span>
      <input className="field" value={value} onChange={(e) => onChange(e.target.value)} />
    </label>
  );
}

function NumberField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
}) {
  return (
    <label className="block">
      <span className="field-label">{label}</span>
      <input
        type="number"
        min={1}
        max={30}
        className="field num"
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
      />
    </label>
  );
}

function Select<T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: T;
  options: { value: T; label: string }[];
  onChange: (v: T) => void;
}) {
  return (
    <label className="block">
      <span className="field-label">{label}</span>
      <select
        className="field"
        value={value}
        onChange={(e) => onChange(e.target.value as T)}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value} className="bg-night">
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}

export default function AdminPage() {
  const [locale, setLocale] = useState<Locale>("it");
  const [theme, setTheme] = useState<CertificateTheme>("glass");
  const [form, setForm] = useState<FormState>(INITIAL);
  const [html, setHtml] = useState<string>("");

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const record = useMemo(() => toRecord(form, locale), [form, locale]);

  useEffect(() => {
    let alive = true;
    renderCertificateHtml(record, locale, theme).then((out) => {
      if (alive) setHtml(out);
    });
    return () => {
      alive = false;
    };
  }, [record, locale, theme]);

  const d = i18n[locale];

  function openPrintable() {
    const w = window.open("", "_blank");
    if (!w) return;
    w.document.write(html);
    w.document.close();
  }

  return (
    <div className="app-surface min-h-screen px-4 py-8 sm:px-6 sm:py-12">
      <div className="mx-auto max-w-[1500px]">
        <header className="flex flex-wrap items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-3">
            <BrandMark className="h-11 w-11" />
            <div>
              <div className="brand-wordmark text-[0.7rem] text-paper">VERSUS</div>
              <div className="text-[0.55rem] uppercase tracking-[0.3em] text-ice/60">
                Surface ID · Admin
              </div>
            </div>
          </Link>

          <div className="flex flex-wrap items-center gap-2">
            <div className="flex gap-1 rounded-full border border-white/15 p-0.5">
              {LOCALES.map((l) => (
                <button
                  key={l}
                  type="button"
                  onClick={() => setLocale(l)}
                  className={cn(
                    "rounded-full px-3 py-1 text-[0.62rem] font-semibold uppercase tracking-[0.16em] transition",
                    locale === l ? "bg-white/15 text-paper" : "text-ice/60",
                  )}
                >
                  {l}
                </button>
              ))}
            </div>
            <div className="flex gap-1 rounded-full border border-white/15 p-0.5">
              {(["glass", "heritage"] as const).map((th) => (
                <button
                  key={th}
                  type="button"
                  onClick={() => setTheme(th)}
                  className={cn(
                    "rounded-full px-3 py-1 text-[0.62rem] font-semibold uppercase tracking-[0.16em] transition",
                    theme === th ? "bg-white/15 text-paper" : "text-ice/60",
                  )}
                >
                  {th}
                </button>
              ))}
            </div>
            <button
              type="button"
              onClick={openPrintable}
              className="inline-flex items-center gap-2 rounded-full bg-magenta px-4 py-2 text-[0.62rem] font-semibold uppercase tracking-[0.14em] text-paper transition hover:brightness-110"
            >
              <Printer className="h-3.5 w-3.5" />
              Print / PDF
            </button>
          </div>
        </header>

        <div className="mt-8 grid gap-6 xl:grid-cols-[420px_1fr]">
          <div className="glass-panel-strong h-fit p-5">
            <h2 className="text-[0.62rem] font-semibold uppercase tracking-[0.22em] text-goldbright">
              Certificate record
            </h2>

            <div className="mt-4 grid gap-3">
              <div className="grid grid-cols-2 gap-3">
                <Text label="Serial" value={form.serial} onChange={(v) => set("serial", v)} />
                <NumberField
                  label="Revision"
                  value={form.revision}
                  onChange={(v) => set("revision", v)}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Text
                  label="Issue date"
                  value={form.issuedAt}
                  onChange={(v) => set("issuedAt", v)}
                />
                <Select
                  label="Status"
                  value={form.status}
                  onChange={(v) => set("status", v)}
                  options={[
                    { value: "valid", label: "Valid" },
                    { value: "superseded", label: "Superseded" },
                    { value: "revoked", label: "Revoked" },
                  ]}
                />
              </div>

              <Text label="Project" value={form.projectName} onChange={(v) => set("projectName", v)} />
              <Text label="Client" value={form.client} onChange={(v) => set("client", v)} />
              <div className="grid grid-cols-2 gap-3">
                <Text label="Site address" value={form.site} onChange={(v) => set("site", v)} />
                <Text label="City" value={form.city} onChange={(v) => set("city", v)} />
              </div>

              <hr className="hairline my-1" />

              <Text label="Surface" value={form.surfaceName} onChange={(v) => set("surfaceName", v)} />
              <div className="grid grid-cols-2 gap-3">
                <Text label="Area (m²)" value={form.m2} onChange={(v) => set("m2", v)} />
                <Select
                  label="Type"
                  value={form.type}
                  onChange={(v) => set("type", v)}
                  options={SURFACE_TYPES.map((k) => ({
                    value: k,
                    label: (d.surfaceType as Record<string, string>)[k] ?? k,
                  }))}
                />
              </div>
              <Select
                label="Finish"
                value={form.finish}
                onChange={(v) => set("finish", v)}
                options={FINISH_KEYS.map((k) => ({
                  value: k,
                  label: (d.finish as Record<string, string>)[k] ?? k,
                }))}
              />
              <Select
                label="Colour — Mediterranean Collection"
                value={form.colorRef}
                onChange={(v) => set("colorRef", v)}
                options={medColors.map((c) => ({ value: c.name, label: `${c.name} · ${c.hex}` }))}
              />

              <div>
                <span className="field-label">VERSUS system</span>
                <div className="flex flex-wrap gap-1.5">
                  {SYSTEM_KEYS.map((s) => {
                    const on = form.systems.includes(s);
                    return (
                      <button
                        key={s}
                        type="button"
                        onClick={() =>
                          set(
                            "systems",
                            on
                              ? form.systems.filter((x) => x !== s)
                              : [...form.systems, s],
                          )
                        }
                        className={cn(
                          "rounded-full border px-2.5 py-1 text-[0.6rem] uppercase tracking-[0.12em] transition",
                          on
                            ? "border-pink/60 bg-pink/15 text-paper"
                            : "border-white/15 text-ice/60",
                        )}
                      >
                        {(d.system as Record<string, string>)[s] ?? s}
                      </button>
                    );
                  })}
                </div>
              </div>

              <hr className="hairline my-1" />

              <Text label="Applicator" value={form.company} onChange={(v) => set("company", v)} />
              <div className="grid grid-cols-2 gap-3">
                <Select
                  label="Academy"
                  value={form.academy}
                  onChange={(v) => set("academy", v)}
                  options={ACADEMY_KEYS.map((k) => ({
                    value: k,
                    label: (d.academy as Record<string, string>)[k] ?? k,
                  }))}
                />
                <Text
                  label="Credential no."
                  value={form.patentino}
                  onChange={(v) => set("patentino", v)}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Text label="Installed" value={form.installed} onChange={(v) => set("installed", v)} />
                <Text label="Collaudo" value={form.collaudo} onChange={(v) => set("collaudo", v)} />
              </div>

              <hr className="hairline my-1" />

              <div className="grid grid-cols-2 gap-3">
                <NumberField
                  label="Warranty · Adhesion"
                  value={form.yearsAdhesion}
                  onChange={(v) => set("yearsAdhesion", v)}
                />
                <NumberField
                  label="Warranty · Anti-crack"
                  value={form.yearsAntiCrack}
                  onChange={(v) => set("yearsAntiCrack", v)}
                />
                <NumberField
                  label="Warranty · UV"
                  value={form.yearsUv}
                  onChange={(v) => set("yearsUv", v)}
                />
                <NumberField
                  label="Warranty · Waterproof"
                  value={form.yearsWaterproof}
                  onChange={(v) => set("yearsWaterproof", v)}
                />
              </div>

              <Text label="VERSUS reviewer" value={form.reviewer} onChange={(v) => set("reviewer", v)} />
              <Text
                label="Insurance policy ref."
                value={form.insurance}
                onChange={(v) => set("insurance", v)}
              />
            </div>
          </div>

          <div className="min-w-0">
            <div className="mb-3 flex items-center gap-2 text-[0.62rem] uppercase tracking-[0.2em] text-ice/60">
              <Palette className="h-3.5 w-3.5" />
              Live preview — {theme} · {locale}
            </div>
            <div className="glass-panel overflow-hidden p-2 sm:p-3">
              {html ? (
                <CertificatePreview html={html} />
              ) : (
                <div className="grid h-64 place-items-center text-xs text-ice/50">
                  Rendering…
                </div>
              )}
            </div>
            <p className="mt-3 text-[0.65rem] leading-relaxed text-ice/50">
              The preview uses the production template (<code>packages/brand/certificate</code>).
              “Print / PDF” opens the same document for the browser’s print-to-PDF. The server
              renders the downloadable PDF with Chromium.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
